import * as Analytics from 'analytics/analytics'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import SolidGoogleIcon from 'LuxKit/Icons/solid/SolidGoogleIcon'
import { useAppSelector } from 'hooks/reduxHooks'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { SOCIAL_LOGIN_ICON_BUTTON_PROPS, SOCIAL_LOGIN_TEXT_BUTTON_PROPS } from './SocialLoginButton'
import config from 'constants/config'
import useWhiteLabelAppConfig from 'hooks/useWhiteLabelAppConfig'
import { postMessageToWhiteLabelApp } from 'lib/whitelabels/whitelabels'

const RelativeTextButton = styled(TextButton)`
  position: relative; /** so that the absolutely positioned span can sit properly */
  overflow: hidden;
`

const RelativeIconButton = styled(IconButton)`
  position: relative; /** so that the absolutely positioned span can sit properly */
  overflow: hidden;
`

const StylesSpan = styled.span`
  position: absolute;
  top: 0;
  bottom: 0;
  opacity: 0.01;
`

interface Props {
  iconOnly?: boolean;
}

function GoogleLoginButton({ iconOnly }: Props) {
  const gsiLoaded = useAppSelector(state => state.auth.gsiLoaded)
  const whiteLabelAppConfig = useWhiteLabelAppConfig()
  const [element, setElement] = useState<HTMLElement | null>(null)
  const [hasInitialized, setHasInitialized] = useState<boolean>(false)

  const clickHandler = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'sign_up_sso_click',
      action: 'google_auth',
      category: 'logging',
      type: 'operational',
    })
  }, [])

  // For review/canary apps, redirect to shared auth domain for Google OAuth
  const handleSharedAuthRedirect = useCallback(() => {
    clickHandler()
    const returnTo = encodeURIComponent(window.location.origin)
    window.location.href = `https://${config.GOOGLE_AUTH_REDIRECT_DOMAIN}/google/auth/init?return_to=${returnTo}`
  }, [clickHandler])

  // Inside the white label app, hand the flow over so the app can run Google auth in a native sheet.
  const handleWhiteLabelAppLogin = useCallback(() => {
    clickHandler()
    postMessageToWhiteLabelApp({ googleLogin: true })
  }, [clickHandler])

  // Both the white label app and review/canary apps take over the click rather than letting GSI drive it
  const overrideClickHandler = whiteLabelAppConfig.isApp ?
    handleWhiteLabelAppLogin :
    config.GOOGLE_AUTH_REDIRECT_DOMAIN ? handleSharedAuthRedirect : undefined

  useEffect(() => {
    // Skip Google button rendering for review/canary apps and white label apps - the login is handled by the LEBT not GSI
    if (config.GOOGLE_AUTH_REDIRECT_DOMAIN || whiteLabelAppConfig.isApp) {
      return
    }

    if (!hasInitialized && gsiLoaded && element) {
      window.google.accounts.id.renderButton(
        element,
        {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          logo_alignment: 'center',
          click_listener: clickHandler,
          width: iconOnly ? 100 : 600,
        },
      )
      setHasInitialized(true)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gsiLoaded, element])

  // Render a simple button wherever the click is handled by us rather than by GSI
  if (overrideClickHandler) {
    return <>
      {!iconOnly && <TextButton
        {...SOCIAL_LOGIN_TEXT_BUTTON_PROPS}
        startIcon={<SolidGoogleIcon />}
        onClick={overrideClickHandler}
      >
        Google
      </TextButton>}
      {iconOnly && <IconButton
        {...SOCIAL_LOGIN_ICON_BUTTON_PROPS}
        onClick={overrideClickHandler}
      >
        <SolidGoogleIcon />
      </IconButton>}
    </>
  }

  return <>
    {!iconOnly && <RelativeTextButton
      {...SOCIAL_LOGIN_TEXT_BUTTON_PROPS}
      startIcon={<SolidGoogleIcon />}
    >
      Google
      <StylesSpan ref={setElement} />
    </RelativeTextButton>}
    {iconOnly && <RelativeIconButton
      {...SOCIAL_LOGIN_ICON_BUTTON_PROPS}
    >
      <StylesSpan ref={setElement} />
      <SolidGoogleIcon />
    </RelativeIconButton>}
  </>
}

export default GoogleLoginButton
